import { useSyncExternalStore } from 'react';

// ── Types ──────────────────────────────────────────────────────
interface OnboardingState {
  completed: boolean;
  completedAt: string | null; // ISO date string
}

const STORAGE_KEY = 'caro_onboarding';

const DEFAULT_STATE: OnboardingState = {
  completed: false,
  completedAt: null,
};

// ── Module-level store ─────────────────────────────────────────
let state: OnboardingState = { ...DEFAULT_STATE };
const listeners = new Set<() => void>();

function loadFromStorage(): OnboardingState {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return { ...DEFAULT_STATE };
    const parsed = JSON.parse(raw);
    return {
      completed: typeof parsed.completed === 'boolean' ? parsed.completed : false,
      completedAt: typeof parsed.completedAt === 'string' ? parsed.completedAt : null,
    };
  } catch {
    return { ...DEFAULT_STATE };
  }
}

function saveToStorage() {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(state));
  } catch {
    // localStorage not available
  }
}

function notify() {
  listeners.forEach((fn) => fn());
}

function update(partial: Partial<OnboardingState>) {
  state = { ...state, ...partial };
  saveToStorage();
  notify();
}

// Initialize on module load
state = loadFromStorage();

// ── Public API ─────────────────────────────────────────────────
function subscribe(callback: () => void) {
  listeners.add(callback);
  return () => { listeners.delete(callback); };
}

function getSnapshot(): OnboardingState {
  return state;
}

export function useOnboarding() {
  const current = useSyncExternalStore(subscribe, getSnapshot);

  return {
    isCompleted: current.completed,
    completedAt: current.completedAt,

    completeOnboarding() {
      update({ completed: true, completedAt: new Date().toISOString() });
    },

    resetOnboarding() {
      update({ completed: false, completedAt: null });
    },
  };
}
